import React, { useState, useEffect } from 'react';
import './InventoryMeds.css';
import { WardDetails } from '../../Datas/WardDetails';
import AddToIndentModal from '../AddToIndentModal/AddToIndentModal';

// Assuming you have icon files for 'add-to-cart', 'add-to-indent', 'view-details'
import AddToCartIconward from '../../assets/logo/customerscart.png';
import ViewDetailsIcon from '../../assets/logo/viewdetails.png';
import AddToIndentIcon from '../../assets/logo/indentadder.png';

const InventoryMedsWards = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [filteredMeds, setFilteredMeds] = useState(WardDetails.flatMap(ward => ward.inventory));
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [category] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [selectedColorFilter, setSelectedColorFilter] = useState('medicine-status-ok');

  const categories = {
    'Seasonal Diseases': ['fever', 'cough', 'cold'],
    'Cough & Cold': ['cough', 'throat pain', 'cold'],
    'Pain & Fever': ['pain', 'fever'],
    'Antibiotics': ['infection', 'antibiotic'],
    'Allergy': ['allergy', 'itching', 'hives'],
    'Vitamins': ['vitamins', 'nutrition'],
    'Digestive': ['indigestion', 'stomach', 'acid reflux'],
    'Skin Care': ['acne', 'dry skin', 'eczema'],
    'Eye Care': ['eye drops', 'vision', 'dry eyes'],
    'Oral Health': ['toothache', 'gum', 'mouthwash'],
    'Mental Health': ['stress', 'anxiety', 'depression'],
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const currentDate = new Date();
    const expiry = new Date(expiryDate.split('/').reverse().join('-'));
    const diffTime = expiry - currentDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatExpiryDate = (expiryDate) => {
    const daysUntilExpiry = getDaysUntilExpiry(expiryDate);
    const status = getStockColor({ expiryDate }); // Get the status (color) for the medicine
  
    if (status === 'medicine-status-expired' || status === 'medicine-status-expiring-soon') {
      if (daysUntilExpiry < 0) {
        return `EXPIRED`; // Just return "EXPIRED" for expired medicines
      }
      return `${daysUntilExpiry} days left`; // Show days left for expiring soon medicines
    }
  
    return `${expiryDate}`; // For others, just show the expiry date without the days
  };
  
  

  const filterMeds = (searchTerm, tags, category, colorFilter) => {
    let filtered = WardDetails.flatMap(ward => ward.inventory);
  
    if (category) {
      filtered = filtered.filter((med) =>
        med.description.toLowerCase().includes(category.toLowerCase()) ||
        med.tags.some((tag) => tag.toLowerCase().includes(category.toLowerCase()))
      );
    }
  
    if (searchTerm) {
      filtered = filtered.filter((med) =>
        med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        med.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
  
    if (tags.length > 0) {
      filtered = filtered.filter((med) =>
        tags.every((tag) => med.tags.includes(tag))
      );
    }
  
    if (colorFilter) {
      filtered = filtered.filter((med) => getStockColor(med) === colorFilter);
    }
  
    // Sort the filtered meds by expiry date, ensuring expired ones come last
    filtered.sort((a, b) => {
      const expiryDiffA = getDaysUntilExpiry(a.expiryDate);
      const expiryDiffB = getDaysUntilExpiry(b.expiryDate);
  
      // If one is expired and the other is not, expired comes last
      if (expiryDiffA < 0 && expiryDiffB >= 0) return 1; // Expired A goes to the bottom
      if (expiryDiffA >= 0 && expiryDiffB < 0) return -1; // Expired B goes to the bottom
  
      // If both are expired or both are not expired, sort by days until expiry
      return expiryDiffA - expiryDiffB;
    });
  
    setFilteredMeds(filtered);
  };
  

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTagSelect = (tag) => {
    setSelectedTags((prevTags) => {
      const newTags = [...prevTags];
      if (newTags.includes(tag)) {
        return newTags.filter((t) => t !== tag);
      } else {
        newTags.push(tag);
        return newTags;
      }
    });
  };

  const handleRemoveTag = (tag) => {
    setSelectedTags(selectedTags.filter((t) => t !== tag));
  };

  const handleOpenModal = (medicine) => {
    setSelectedMedicine(medicine);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMedicine(null);
  };

  const handleAddToIndent = (medicine, quantity) => {
    console.log(`Added ${quantity} of ${medicine.name} to indent`);
  };

  const handleAddToSpecialIndent = (medicine, specialInstructions) => {
    console.log(`Added ${medicine.name} to special indent with instructions: ${specialInstructions}`);
  };

  const handleShowAll = () => {
    setSelectedColorFilter(''); // Reset the filter to show all items
  };

  useEffect(() => {
    filterMeds(searchTerm, selectedTags, category, selectedColorFilter);
  }, [searchTerm, selectedTags, category, selectedColorFilter]);

  const getStockColor = (med) => {
    const daysUntilExpiry = getDaysUntilExpiry(med.expiryDate);

    if (med.stockLevel < 1) {
      return 'medicine-status-low-stock';
    } else if (daysUntilExpiry < 0) {
      return 'medicine-status-expired';
    }else if (daysUntilExpiry <= 100) {
      return 'medicine-status-expiring-soon';
    } else if (med.stockLevel < 5) {
      return 'medicine-status-low-stock';
    } 
    return 'medicine-status-ok';
  };


  return (
    <div className="inventory-container">
      <div className="header">
        <div className="category-dropdown">
          <button
            className="dropdown-button"
            onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
          >
            {category || 'Select Category'}
          </button>
          {categoryDropdownOpen && (
            <div className="dropdown-content">
              <div className="category-panel">
                {Object.keys(categories).map((categoryName) => (
                  <div className="category-section" key={categoryName}>
                    <h3>{categoryName}</h3>
                    <div className="tags-container">
                      {categories[categoryName].map((tag, index) => (
                        <button
                          key={index}
                          className={`tag-button ${selectedTags.includes(tag) ? 'selected' : ''}`}
                          onClick={() => handleTagSelect(tag)}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button className="add-med-button-inventory">Add Med</button>

        <div className="search-bar-inventory">
          <input
            type="text"
            placeholder="Search medicines..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <div className="selected-tags">
        {selectedTags.map((tag, index) => (
          <span key={index} className="tag-capsule">
            {tag}
            <button className="tag-remove" onClick={() => handleRemoveTag(tag)}>&times;</button>
          </span>
        ))}
      </div>

      <div className="color-filter-container">
        <button
          className={`color-filter-button ${selectedColorFilter === 'medicine-status-expired' ? 'selected' : ''}`}
          onClick={() => setSelectedColorFilter('medicine-status-expired')}
        >
          Expired
        </button>
        <button
          className={`color-filter-button ${selectedColorFilter === 'medicine-status-low-stock' ? 'selected' : ''}`}
          onClick={() => setSelectedColorFilter('medicine-status-low-stock')}
        >
          Low Stock
        </button>
        <button
          className={`color-filter-button ${selectedColorFilter === 'medicine-status-expiring-soon' ? 'selected' : ''}`}
          onClick={() => setSelectedColorFilter('medicine-status-expiring-soon')}
        >
          Expiring Soon
        </button>
        <button
          className={`color-filter-button ${selectedColorFilter === 'medicine-status-ok' ? 'selected' : ''}`}
          onClick={() => setSelectedColorFilter('medicine-status-ok')}
        >
          Filled
        </button>
        <button
  className={`color-filter-button ${selectedColorFilter === '' ? 'selected' : ''}`}  // This ensures Show All is selected by default
  onClick={handleShowAll}
>
  Show All
</button>
      </div>

      <table className="inventory-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Batch Number</th>
            <th>Medicine Name</th>
            <th>Expiry Date</th>
            <th>Stock(Tablets)</th>
            <th>Tags</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredMeds.map((med, index) => (
            <tr key={index} className={getStockColor(med)}>
              {/* Dot for Status */}
              <td className="status-dot">
                <span className={`dot ${getStockColor(med)}`}></span>
              </td>
              <td>{med.batchNumber}</td>
              <td>{med.name}</td>
              <td>{formatExpiryDate(med.expiryDate)}</td>
              <td>{med.stockLevel}</td>
              <td>
                {med.tags.map((tag, i) => (
                  <span key={i} className="tag-capsuless">
                    {tag}
                  </span>
                ))}
              </td>
              <td>
                <button className="view-details-button-inventory3">
                  <img src={ViewDetailsIcon} alt="View Details" className="icon-inventory" />
                </button>
                <button
                  className="add-to-indent-button"
                  onClick={() => handleOpenModal(med)}
                >
                  <img src={AddToIndentIcon} alt="Add to Indent" className="icon-inventory" />
                </button>
                <button className="add-to-cart-button-inventory3">
                  <img src={AddToCartIconward} alt="Add to Cart" className="icon-inventory" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>


      <AddToIndentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        medicine={selectedMedicine}
        onAddToIndent={handleAddToIndent}
        onAddToSpecialIndent={handleAddToSpecialIndent}
      />
    </div>
  );
};

export default InventoryMedsWards;
